import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  AutosuggestProps,
  Store,
  StoreUpdateInput,
  VendorsSearchItem
} from "@bluframe/mightychroma-mightytool-frontend-types"
import useItemView, { UseItemViewReturn } from "hooks/useListItemView"
import { FormEventHandler, RefCallback, useCallback, useRef } from "react"

import Item from "./Item"

export interface Props {
  isAdd?: boolean
  isAdmin?: boolean
  isEditing?: boolean
  isEditor?: boolean
  isSaveLoading?: boolean
  isVendorsSuggestionsLoading?: boolean
  item?: Store
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void
  onGetVendorsSuggestions: AutosuggestProps["onGetSuggestions"]
  onPublishToggle?: () => void

  onSave: (input: StoreUpdateInput) => void

  onSelectVendorId: (id: string, name: string) => void
  selectedVendorId?: string
  vendors: VendorsSearchItem[]
}

export interface ComponentProps
  extends
    Omit<
      Props,
      | "isEditing"
      | "isSaveLoading"
      | "onCancel"
      | "onDelete"
      | "onEdit"
      | "onPublishToggle"
      | "onSave"
    >,
    Omit<UseItemViewReturn, "item" | "refs"> {
  isActionButtonLoading?: boolean
  onSubmit: FormEventHandler
  urlRef: RefCallback<HTMLInputElement>
}

const usePrepareComponent = ({
  isAdd,
  isEditing,
  isSaveLoading,
  item,
  onCancel,
  onDelete,
  onEdit,
  onGetVendorsSuggestions,
  onPublishToggle,
  onSave,
  onSelectVendorId,
  selectedVendorId,
  vendors,
  ...props
}: Props): ComponentProps => {
  const {
    actionButtonOnClick,
    actionButtonLabel,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick
  } = useItemView<Store>({
    fields: [],
    isAdd,
    isEditing,
    isSaveLoading,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle
  })

  const urlInputRef = useRef<HTMLInputElement | null>(null)
  const urlRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    urlInputRef.current = node
  }, [])

  const onSubmit: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault()
      if (!urlInputRef.current?.value || !selectedVendorId) {
        return
      }
      onSave({
        url: urlInputRef.current.value,
        vendorId: selectedVendorId
      })
    },
    [onSave, selectedVendorId]
  )

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    isActionButtonLoading,
    isAdd,
    isEditMode,
    isModalOpen,
    item,
    modalContent,
    onGetVendorsSuggestions,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    onSelectVendorId,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    selectedVendorId,
    urlRef,
    vendors
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
